/* eslint-disable react/jsx-no-constructed-context-values */
import React, { Context, createContext, ReactElement, useEffect, useState, Dispatch } from "react"
import { SingleValue } from "react-select"

import {
  useGetBaseXlsFormWithGroupsQuery,
  useGetDiscardedFormFieldsQuery,
  useUpdateFieldDiscardedMutation,
} from "services/household/xlsFormSchemas"

import { FormField, MappingField, OptionGroup } from "interfaces/mappingInterfaces"

import { getGroupList, searchByString } from "utils/helpers"

export enum TypeFilter {
  ALL = "ALL",
  UNMAPPED = "UNMAPPED",
  MAPPED = "MAPPED",
  DISCARDED = "DISCARDED",
}

type MappedFields = Record<string, MappingField>
export interface IXslMappingContext {
  isLoaded?: boolean
  fieldsList: MappingField[]
  mappedField: MappedFields
  handleSearch: (value: string, coValue?: string) => void
  handleDiscardField: (mappingId: number) => Promise<void>
  setFilterType: Dispatch<TypeFilter>
  filterType: TypeFilter
  setCurrentGroupFilter: Dispatch<SingleValue<OptionGroup>>
  currentGroupFilter: SingleValue<OptionGroup> | null
  totalFields: number
  handleAddCalculatedField: (field: FormField, index: number) => void
  handleRemoveCalculatedField: (field: MappingField, index: number, calculatedIndex: number) => void
  handleAddFormulaField: (field: MappingField, formula: string, index: number) => void
  currentGroupFilterPopover: SingleValue<OptionGroup> | null
  setCurrentGroupFilterPopover: Dispatch<SingleValue<OptionGroup>>
  inputSearchPopover: string
  setInputSearchPopover: Dispatch<string>
  optionsGroup: OptionGroup[]
  inputSearch: string
  setInputSearch: Dispatch<string>
}

export const XslMappingContext: Context<IXslMappingContext> = createContext<IXslMappingContext>({
  isLoaded: false,
  fieldsList: [],
  mappedField: {},
  handleSearch: () => {},
  handleDiscardField: () => new Promise(() => {}),
  setFilterType: () => {},
  setCurrentGroupFilter: () => {},
  filterType: TypeFilter.ALL,
  currentGroupFilter: null,
  totalFields: 0,
  handleAddCalculatedField: () => {},
  handleRemoveCalculatedField: () => {},
  handleAddFormulaField: () => {},
  currentGroupFilterPopover: null,
  setCurrentGroupFilterPopover: () => {},
  inputSearchPopover: "",
  setInputSearchPopover: () => {},
  optionsGroup: [],
  inputSearch: "",
  setInputSearch: () => {},
})

const XslMappingProvider = ({
  xlsFormId,
  children,
}: {
  xlsFormId: number
  children: ReactElement
}): React.ReactElement => {
  const [xlsFormSchema, setXlsFormSchema] = useState<MappingField[]>([])
  const [mappedField, setMappedField] = useState<MappedFields>({})

  const [filterType, setFilterType] = useState<TypeFilter>(TypeFilter.ALL)
  const [optionsGroup, setOptionsGroup] = useState<OptionGroup[]>([])
  const [currentGroupFilter, setCurrentGroupFilter] = useState<SingleValue<OptionGroup> | null>(null)
  const [currentGroupFilterPopover, setCurrentGroupFilterPopover] = useState<SingleValue<OptionGroup> | null>(null)
  const [inputSearchPopover, setInputSearchPopover] = useState<string>("")
  const [inputSearch, setInputSearch] = useState<string>("")
  const [isLoaded, setIsLoaded] = useState<boolean>(false)

  // MAP LIST
  const { data: xslFormSchemaData } = useGetBaseXlsFormWithGroupsQuery({
    xlsFormId: Number(xlsFormId),
  })
  // DISCARDED LIST
  const { data: discardedFieldsData } = useGetDiscardedFormFieldsQuery({ xlsFormId: Number(xlsFormId) })
  // MUTATION DISCARD FIELD
  const [discardField] = useUpdateFieldDiscardedMutation()

  const selectList = (value: string): MappingField[] => {
    switch (value) {
      case TypeFilter.DISCARDED:
        return discardedFieldsData ?? []
      case TypeFilter.ALL:
        return xslFormSchemaData ?? []
      case TypeFilter.MAPPED:
        return xslFormSchemaData?.filter(field => field.fieldName || field.formula) ?? []
      default:
        return xslFormSchemaData?.filter(field => !field.fieldName && !field.formula) ?? []
    }
  }

  // SEARCH & FILTERING
  const handleSearch = (value: string, coValue?: string): void => {
    let list = selectList(filterType)
    setOptionsGroup(getGroupList(list || [], "groupName"))
    if (currentGroupFilter) list = list?.filter(field => field.groupName?.includes(currentGroupFilter?.label || ""))
    const filteredFields = searchByString(list ?? [], value, ["baseFieldName", "baseFieldLabel"])
    const filteredFieldsCO = coValue ? searchByString(filteredFields ?? [], coValue, ["fieldName"]) : undefined
    setXlsFormSchema(filteredFieldsCO || filteredFields)
  }

  // DISCARDED/UN-DISCARDED FIELD
  const handleDiscardField = async (mappingId: number): Promise<void> => {
    try {
      await discardField({ xlsFormMappingId: mappingId, discarded: filterType !== TypeFilter.DISCARDED }).unwrap()
    } catch {
      // Ignore discard failures; the mutation hook exposes request state elsewhere.
    }
  }

  const handleAddFormulaField = (field: MappingField, formula: string, index: number): void => {
    const newField = { ...field, formula }
    const newFields = [...xlsFormSchema]
    newFields[index] = newField
    setXlsFormSchema(newFields)
  }

  const handleAddCalculatedField = (field: FormField, index: number): void => {
    setIsLoaded(false)
    const newField = {
      ...xlsFormSchema[index],
      formulaFields: [
        ...(xlsFormSchema[index]?.formulaFields || []),
        {
          fieldId: field.fieldId,
          fieldName: field.fieldName,
          fieldLabel: field.fieldLabel,
        },
      ],
    }

    const newFields = [...xlsFormSchema]
    newFields[index] = newField

    setXlsFormSchema(newFields)
    setTimeout(() => {
      setIsLoaded(true)
    }, 10)
  }

  const handleRemoveCalculatedField = (field: MappingField, index: number, calculatedIndex: number): void => {
    const formulaFields = field?.formulaFields || []
    const newField = {
      ...xlsFormSchema[index],
      formulaFields: [...formulaFields].filter((_, i) => i !== calculatedIndex),
    }

    const newFields = [...xlsFormSchema]
    newFields[index] = newField

    setXlsFormSchema(newFields)
    setTimeout(() => {
      setIsLoaded(true)
    }, 10)
  }

  useEffect(() => {
    handleSearch(inputSearch)
    setMappedField({})
    xslFormSchemaData?.map(field => {
      if (field.fieldName) {
        setMappedField(prevState => ({ ...prevState, [field.fieldName]: field }))
      }
    })
    setIsLoaded(true)
  }, [xslFormSchemaData, discardedFieldsData])

  return (
    <XslMappingContext.Provider
      value={{
        isLoaded,
        fieldsList: xlsFormSchema,
        mappedField,
        handleSearch,
        handleDiscardField,
        setFilterType,
        filterType,
        setCurrentGroupFilter,
        currentGroupFilter,
        totalFields: xslFormSchemaData?.length || 0,
        handleAddCalculatedField,
        handleRemoveCalculatedField,
        handleAddFormulaField,
        currentGroupFilterPopover,
        setCurrentGroupFilterPopover,
        inputSearchPopover,
        setInputSearchPopover,
        optionsGroup,
        inputSearch,
        setInputSearch,
      }}
    >
      {children}
    </XslMappingContext.Provider>
  )
}

export default XslMappingProvider
